import { useState } from "react";
import { LegalShell, LegalSection, SUPPORT_EMAIL } from "../site/LegalShell.jsx";

// Interest-gauging page for owners and managers. There is no leads backend
// yet, so the form composes a prefilled email rather than pretending to save
// anything — a submit button that silently drops enquiries would be worse than
// no form. Swap `submit()` for a POST when a leads endpoint exists; the field
// list is already the shape you'd store.
const ISLANDS = ["Oʻahu", "Maui", "Kauaʻi", "Hawaiʻi Island", "Molokaʻi", "Lānaʻi"];
const TYPES = ["Hotel or resort", "Condo", "Vacation rental home", "Villa", "Bed & breakfast", "Something else"];

const FIELDS = [
  { key: "name", label: "Your name", required: true },
  { key: "email", label: "Email", type: "email", required: true },
  { key: "phone", label: "Phone (optional)", type: "tel" },
  { key: "property", label: "Property name", required: true },
  { key: "island", label: "Island", options: ISLANDS, required: true },
  { key: "type", label: "Property type", options: TYPES, required: true },
  { key: "units", label: "Rooms or units", placeholder: "e.g. 1, or 40" },
  { key: "listing", label: "Existing listing link (optional)", placeholder: "https://" },
  { key: "notes", label: "Anything else we should know?", textarea: true }
];

export default function ListPropertyPage() {
  const [form, setForm] = useState(Object.fromEntries(FIELDS.map(f => [f.key, ""])));
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const required = FIELDS.filter(f => f.required);
  const valid = required.every(f => form[f.key].trim()) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

  const submit = e => {
    e.preventDefault();
    if (!valid) return;
    const body = FIELDS
      .filter(f => form[f.key].trim())
      .map(f => `${f.label.replace(/ \(optional\)$/, "")}: ${form[f.key].trim()}`)
      .join("\n");
    const subject = `Property listing enquiry — ${form.property.trim()}`;
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  return (
    <LegalShell
      eyebrow="For owners & managers"
      title="List your Hawaiʻi property"
      intro="We're a Hawaiʻi-only booking site, and we're opening up direct listings. If you run a hotel, condo, or vacation rental on any of the six islands, tell us about it and we'll get in touch."
    >
      <LegalSection heading="Why us">
        <p>
          Every traveler on this site is already looking for Hawaiʻi. They aren't comparing your place against
          somewhere in Cabo. That's the whole reason we built a site for six islands instead of the world.
        </p>
        <p>
          We also negotiate rates directly with properties through our supply partner, so some of what we list
          isn't available on the big booking sites. If you have room to offer something sharper than your public
          rate, we can put it in front of an audience that only wants Hawaiʻi.
        </p>
      </LegalSection>

      <LegalSection heading="Where this is at">
        <p>
          Being straight with you: we're building the list now and onboarding properties in batches rather than
          all at once. Sending this form doesn't put your place live and doesn't commit either of us to anything.
          It starts a conversation, and we'll come back to you about terms and timing.
        </p>
      </LegalSection>

      <div className="card p-6 md:p-8">
        <h2 className="font-display text-xl md:text-2xl leading-tight text-ink">Tell us about your property</h2>
        <p className="prose-body mt-2 text-sm">Takes a minute. Nothing here is published anywhere.</p>

        <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(f => (
            <label key={f.key} className={`flex flex-col gap-1.5 ${f.textarea || f.key === "listing" ? "sm:col-span-2" : ""}`}>
              <span className="text-xs font-medium text-ink-soft">
                {f.label}{f.required && <span className="text-sunset-600"> *</span>}
              </span>
              {f.options ? (
                <select value={form[f.key]} onChange={e => set(f.key, e.target.value)} className="pill-input px-3 py-2.5 text-sm" required={f.required}>
                  <option value="">Select…</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.textarea ? (
                <textarea
                  value={form[f.key]} onChange={e => set(f.key, e.target.value)} rows={3}
                  placeholder="Location, what makes it special, how you handle bookings today…"
                  className="rounded-2xl border border-ink/10 bg-white px-4 py-2.5 text-sm text-ink placeholder:text-ink-muted focus:border-ocean-400 focus:outline-none focus:ring-2 focus:ring-ocean-200 w-full resize-none"
                />
              ) : (
                <input
                  type={f.type || "text"} value={form[f.key]} onChange={e => set(f.key, e.target.value)}
                  placeholder={f.placeholder} required={f.required}
                  className="pill-input px-3 py-2.5 text-sm"
                />
              )}
            </label>
          ))}

          <div className="sm:col-span-2 flex flex-col gap-3">
            <button type="submit" disabled={!valid} className="btn-primary w-full py-3 text-base disabled:opacity-50 sm:w-auto sm:self-start sm:px-8">
              Send enquiry
            </button>
            {sent ? (
              <p className="text-sm text-ocean-700">
                Your email app should have opened with the details filled in. If nothing happened, email{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="underline underline-offset-2">{SUPPORT_EMAIL}</a> directly.
              </p>
            ) : (
              <p className="text-[11px] text-ink-muted">
                This opens your email app with the details filled in, so you can see exactly what you're sending.
                Prefer to write it yourself? Email{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="underline underline-offset-2 text-ocean-700">{SUPPORT_EMAIL}</a>.
              </p>
            )}
          </div>
        </form>
      </div>
    </LegalShell>
  );
}
